
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  PlayIcon,
  XMarkIcon,
  CalendarIcon,
  TagIcon,
  EyeIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import { getImageUrlWithEnv } from '../../utils/imageUrl';

// API configuration
const getApiUrl = (endpoint) => {
  const baseUrl = 'https://fciveria.ge';
  return `${baseUrl}${endpoint}`;
};

// YouTube URL utilities
const extractYouTubeId = (url) => {
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return (match && match[2].length === 11) ? match[2] : null;
};

const isYouTubeUrl = (url) => {
  return url && (url.includes('youtube.com') || url.includes('youtu.be'));
};

const generateYouTubeEmbedUrl = (videoId) => {
  return `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`;
};

const VideoGallery = () => {
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('ყველა');
  const [videos, setVideos] = useState([]);
  const [categories, setCategories] = useState(['ყველა']);
  const [loading, setLoading] = useState(true);
  const [updatedViews, setUpdatedViews] = useState(new Set());

  useEffect(() => {
    loadVideos();
    loadCategories();
    
    // Set up periodic polling to refresh view counts every 10 seconds
    const interval = setInterval(() => {
      loadVideos(false);
    }, 10000);
    
    return () => clearInterval(interval);
  }, []);

  const loadVideos = async (showLoader = true) => {
    try {
      if (showLoader) setLoading(true);
      const response = await fetch(getApiUrl('/api/videos'));
      if (response.ok) {
        const videosData = await response.json();
        // Convert backend format to frontend format
        const formattedVideos = videosData.map(video => ({
          id: video.id,
          thumbnail: video.thumbnailUrl,
          title: video.title,
          category: video.category,
          duration: video.duration,
          views: video.views,
          date: video.uploadDate ? video.uploadDate.split('T')[0] : '',
          description: video.description,
          videoUrl: video.videoUrl
        }));
        
        // Check for view count changes and highlight them
        if (!showLoader && videos.length > 0) {
          const newUpdatedViews = new Set();
          formattedVideos.forEach(newVideo => {
            const oldVideo = videos.find(v => v.id === newVideo.id);
            if (oldVideo && oldVideo.views !== newVideo.views) {
              newUpdatedViews.add(newVideo.id);
            }
          });
          setUpdatedViews(newUpdatedViews);
          // Clear highlights after 3 seconds
          setTimeout(() => setUpdatedViews(new Set()), 3000);
        }
        
        setVideos(formattedVideos);
      }
      if (showLoader) setLoading(false);
    } catch (error) {
      console.error('Error loading videos:', error);
      if (showLoader) setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await fetch(getApiUrl('/api/videos/categories'));
      if (response.ok) {
        const categoriesData = await response.json();
        setCategories(['ყველა', ...categoriesData]);
      }
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const handleVideoClick = async (video) => {
    setSelectedVideo(video);
    // Increment view count
    try {
      await fetch(getApiUrl(`/api/videos/${video.id}`));
      // Immediately refresh videos to show updated view count
      setTimeout(() => {
        loadVideos(false);
      }, 500);
    } catch (error) {
      console.error('Error incrementing views:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-red-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 font-georgian">იტვირთება...</p>
        </div>
      </div>
    );
  }


  const filteredVideos = selectedCategory === 'ყველა' 
    ? videos 
    : videos.filter(video => video.category === selectedCategory);

  const totalViews = videos.reduce((sum, video) => sum + video.views, 0);
  const totalDuration = videos.reduce((sum, video) => {
    const [minutes, seconds] = video.duration.split(':').map(Number);
    return sum + minutes * 60 + seconds;
  }, 0);

  const formatDuration = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return hours > 0 ? `${hours}:${minutes.toString().padStart(2, '0')}:00` : `${minutes}:00`;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 font-georgian mb-4">
            ვიდეო გალერეა
          </h1>
          <p className="text-xl text-gray-600 font-georgian max-w-3xl mx-auto">
            ჩვენი გუნდის საუკეთესო ვიდეო მომენტები, ინტერვიუები და ჰაილაითები
          </p>
        </div>



        {/* Category Filter */}
        <div className="flex justify-center mb-8">
          <div className="bg-white rounded-lg shadow-lg p-2">
            <div className="flex flex-wrap justify-center gap-2">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 rounded-lg font-georgian transition-colors text-sm ${
                    selectedCategory === category
                      ? 'bg-red-600 text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Video Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {filteredVideos.map((video) => (
            <div
              key={video.id}
              className="relative group cursor-pointer bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300"
              onClick={() => handleVideoClick(video)}
            >
              <div className="relative">
                <img
                  src={getImageUrlWithEnv(video.thumbnail)}
                  alt={video.title}
                  className="w-full h-48 object-cover"
                />
                <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
                  <div className="w-16 h-16 bg-white bg-opacity-90 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <PlayIcon className="h-8 w-8 text-red-600 ml-1" />
                  </div>
                </div>
                <div className="absolute bottom-2 right-2 bg-black bg-opacity-75 text-white px-2 py-1 rounded text-xs font-georgian">
                  {video.duration}
                </div>
                {isYouTubeUrl(video.videoUrl) && (
                  <div className="absolute top-2 left-2 bg-red-600 text-white px-2 py-1 rounded text-xs font-georgian flex items-center">
                    <svg className="w-3 h-3 mr-1" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                    </svg>
                    YouTube
                  </div>
                )}
              </div>
              <div className="p-4">
                <h3 className="font-semibold text-gray-900 font-georgian mb-2 text-sm line-clamp-2">{video.title}</h3>
                <p className="text-xs text-gray-600 font-georgian mb-3 line-clamp-2">{video.description}</p>
                <div className="flex items-center justify-between text-xs text-gray-500 font-georgian">
                  <span className="bg-red-100 text-red-600 px-2 py-1 rounded">{video.category}</span>
                  <div className="flex items-center space-x-2">
                        <span className={`flex items-center transition-all duration-500 ${
                          updatedViews.has(video.id) 
                            ? 'text-green-600 font-bold scale-110' 
                            : ''
                        }`}>
                          <EyeIcon className="h-3 w-3 mr-1" />
                          {video.views}
                        </span>
                    <span className="flex items-center">
                      <CalendarIcon className="h-3 w-3 mr-1" />
                      {video.date}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Load More Button */}
        <div className="text-center">
          <button className="bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-8 rounded-lg transition-colors duration-200 font-georgian">
            მეტის ჩატვირთვა
          </button>
        </div>

        {/* Video Modal */}
        {selectedVideo && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="relative max-w-4xl w-full bg-white rounded-lg overflow-hidden">
              <button
                onClick={() => setSelectedVideo(null)}
                className="absolute top-4 right-4 text-gray-600 hover:text-gray-800 z-10 bg-white rounded-full p-2"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
              
              {/* Video Player */}
              <div className="relative bg-black aspect-video">
                {isYouTubeUrl(selectedVideo.videoUrl) ? (
                  <iframe
                    src={generateYouTubeEmbedUrl(extractYouTubeId(selectedVideo.videoUrl))}
                    title={selectedVideo.title}
                    className="w-full h-full"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                ) : (
                  <>
                    <img
                      src={getImageUrlWithEnv(selectedVideo.thumbnail)}
                      alt={selectedVideo.title}
                      className="w-full h-full object-cover opacity-50"
                    />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-20 h-20 bg-red-600 rounded-full flex items-center justify-center cursor-pointer hover:bg-red-700 transition-colors">
                        <PlayIcon className="h-10 w-10 text-white ml-1" />
                      </div>
                    </div>
                    <div className="absolute bottom-4 right-4 bg-black bg-opacity-75 text-white px-3 py-1 rounded font-georgian">
                      {selectedVideo.duration}
                    </div>
                  </>
                )}
              </div>
              
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 font-georgian mb-2">
                  {selectedVideo.title}
                </h3>
                <p className="text-gray-600 font-georgian mb-4">{selectedVideo.description}</p>
                <div className="flex items-center justify-between text-sm text-gray-500 font-georgian">
                  <div className="flex items-center space-x-4">
                    <span className="bg-red-100 text-red-600 px-3 py-1 rounded-full">
                      {selectedVideo.category}
                    </span>
                    <span className="flex items-center">
                      <CalendarIcon className="h-4 w-4 mr-1" />
                      {selectedVideo.date}
                    </span>
                    <span className="flex items-center">
                      <ClockIcon className="h-4 w-4 mr-1" />
                      {selectedVideo.duration}
                    </span>
                  </div>
                  <span className="flex items-center">
                    <EyeIcon className="h-4 w-4 mr-1" />
                    {selectedVideo.views} ნახვა
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="text-center mt-8">
          <Link
            to="/media/social"
            className="inline-flex items-center bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-6 rounded-lg transition-colors duration-200 font-georgian"
          >
            ← უკან მედიაზე
          </Link>
        </div>
      </div>
    </div>
  );
};

export default VideoGallery;
